/**
 * Android WebView multiplies every computed font-size by the system font-scale
 * (Settings → Display → Font size). That is correct and wanted for reading
 * text, but it also inflates fixed-size display type: at the largest step
 * (1.3x) the cellar's hero title renders at ~73px instead of 56px and is
 * clipped by the right edge of the screen.
 *
 * Measuring it lets display type divide the factor back out — the title stays
 * the size it was designed to be — while body copy keeps the larger text the
 * user asked for. A block with `line-height: 1` is exactly `font-size` tall,
 * so the ratio of its rendered height to its specified size is the zoom
 * factor. Font metrics don't enter into it, so this needs no loaded fonts.
 *
 * Measured on a Pixel 10 Pro at the largest font setting: the multiplier is a
 * flat 1.3 from 12px through 100px, so one probe size is representative of the
 * whole scale (Android's non-linear sp scaling does not apply here).
 */
const PROBE_PX = 100;

export function measureTextZoom(): number {
  try {
    const probe = document.createElement('div');
    probe.style.cssText =
      `position:absolute;visibility:hidden;pointer-events:none;top:0;left:-9999px;` +
      `font-size:${PROBE_PX}px;line-height:1;padding:0;border:0;`;
    probe.textContent = 'A';
    document.body.appendChild(probe);
    const zoom = probe.getBoundingClientRect().height / PROBE_PX;
    probe.remove();
    // Guard against a zero/absurd reading (detached body, display:none ancestor).
    return zoom >= 0.5 && zoom <= 4 ? zoom : 1;
  } catch {
    return 1;
  }
}
